import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseMetaColumn, FilterItem, FilterItemOperator, IStorageLocal, NamingConvention, PriceOperation, SetTimeouService, TimeOperation } from "namirasoft-core";
import { DimensionType, GranularityType, GroupedResponseRow, Usage } from "namirasoft-cost";
import { ProductRow } from "namirasoft-payment";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, DateTimeFormatter, IDFormatter, IntegerFormatter, MoneyFormatter, NSBoxBaseCombo, NSBoxCombo, NSBoxDateTimeRange, NSBoxEntity, NSBoxEnum, NSButton, NSChartBar, NSColumn, NSID, NSLabel, NSLoading, NSNoData, NSPanel, NSRow, NSSection, NSTable, NSTitle, StringFormatter, StringFormatterSizeType, TableColumnInfo } from "namirasoft-site-react";
import { WorkspaceRow } from "namirasoft-workspace";
import { Component, createRef } from "react";
import { CostEntities } from "../CostEntities";
import { CostLayout } from "../CostLayout";
import { CostRouterProps } from "../CostRouterProps";
import styles from "./CostExplorerPage.module.css";

interface CostExplorerPageProps extends NSARouterMakerResult, CostRouterProps { }

interface CostExplorerPageState
{
    loading: boolean;
    res: GroupedResponseRow[] | null;
    selected_resource_entities: string[];
    chart_date_labels: string[];
    chart_dimension_data: { label: string, data: number[] }[];
}

class CostExplorerPage extends Component<CostExplorerPageProps, CostExplorerPageState>
{
    private NSBoxDateTimeRange_StartEnd = createRef<NSBoxDateTimeRange>();
    private NSBoxEnum_Granularity = createRef<NSBoxEnum<GranularityType>>();
    private NSBoxEnum_Dimension = createRef<NSBoxEnum<DimensionType>>();
    private NSBoxEntity_WrokspaceID = createRef<NSBoxEntity<WorkspaceRow>>();
    private NSBoxEntity_ProductID = createRef<NSBoxEntity<ProductRow>>();
    private NSBoxBaseCombo_Entity = createRef<NSBoxBaseCombo<{ resource_entity: string }>>();
    private NSBoxBaseCombo_ResourceID = createRef<NSBoxBaseCombo<{ resource_id: string }>>();
    private NSBoxCombo_Usage = createRef<NSBoxCombo>();

    private server_call = new SetTimeouService();

    private NSTable_Result = createRef<NSTable<GroupedResponseRow>>();

    constructor(props: CostExplorerPageProps)
    {
        super(props);
        this.state = {
            res: null,
            loading: false,
            chart_dimension_data: [],
            chart_date_labels: [],
            selected_resource_entities: []
        };
        this.onChanged = this.onChanged.bind(this);
    }

    private getFormattedDateByGranularity(date: Date, granularity: GranularityType): string
    {
        const formatMap: Record<GranularityType, string> = {
            [GranularityType.Hour]: "YYYY/MM/DD H",
            [GranularityType.Day]: "YYYY/MM/DD",
            [GranularityType.Week]: "YYYY WW",
            [GranularityType.Month]: "YYYY MMMM",
        };

        return TimeOperation.format(date, formatMap[granularity]);
    }

    private onChanged()
    {
        this.setState((prev) => ({ ...prev, loading: true }));
        this.server_call.setTimeoutIfNotCalledAgain(() =>
        {
            this.loadData();
        }, 1200);
    };

    private loadData()
    {
        let datetime_range = this.NSBoxDateTimeRange_StartEnd.current?.getValue() ?? null;
        let start_date = datetime_range?.from ?? new Date();
        let end_date = datetime_range?.to ?? new Date();
        let granularity = this.NSBoxEnum_Granularity.current?.getValueEnumOne(GranularityType, GranularityType.Day) ?? GranularityType.Day;
        let dimension = this.NSBoxEnum_Dimension.current?.getValueEnumOne(DimensionType, DimensionType.NONE) ?? DimensionType.NONE;
        let filter_workspace_ids = this.NSBoxEntity_WrokspaceID.current?.getValueAll() ?? [];
        let filter_product_ids = this.NSBoxEntity_ProductID.current?.getValueAll() ?? [];
        let filter_resource_entities = this.NSBoxBaseCombo_Entity.current?.getValueAll() ?? [];
        let filter_resource_ids = this.NSBoxBaseCombo_ResourceID.current?.getValueAll() ?? [];
        let filter_usage_names = this.NSBoxCombo_Usage.current?.getValueAll() ?? [];

        this.props.servers.cost.cost.Grouped({
            start_date,
            end_date,
            granularity,
            dimension,
            filter_workspace_ids,
            filter_product_ids,
            filter_resource_entities,
            filter_resource_ids,
            filter_usage_names,
        }).then(async res =>
        {
            this.setState({ res, chart_dimension_data: [], chart_date_labels: [] }, () =>
            {
                this.NSTable_Result.current?.setColumns(this.getColumns());
                this.NSTable_Result.current?.reload(null, null);
                this.processData();
            });
        }).catch((error) =>
        {
            this.setState((prev) => ({ ...prev, loading: false }));
            this.props.notifier.onError(error as Error);
        });
    }
    private async processData()
    {
        let res = this.state.res ?? [];
        let dimension_type = this.NSBoxEnum_Dimension?.current?.getValueEnumOne(DimensionType, DimensionType.NONE) ?? DimensionType.NONE;
        let granularity_type = this.NSBoxEnum_Granularity?.current?.getValueEnumOne(GranularityType, GranularityType.Day) ?? GranularityType.Day;

        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let translator = async (id: string) =>
        {
            if (dimension_type !== DimensionType.NONE && dimension_type !== DimensionType.RESOURCE_ENTITY && dimension_type !== DimensionType.RESOURCE_ID && dimension_type !== DimensionType.USAGE_NAME)
            {
                let info = NSID.getInfo(id, namirasoft);
                if (info)
                {
                    let cache = NSID.getCache(info, new IStorageLocal(), id)
                    return await cache.get();
                }
            }
            return id;
        }

        let chart_date_labels = this.state.res?.map(row => this.getFormattedDateByGranularity(new Date(row.period), granularity_type)) ?? [];
        chart_date_labels = [...new Set(chart_date_labels)];

        let dimension_data: { [label: string]: number[] } = {};
        let index = 0;
        for (let d = 0; d < chart_date_labels.length; d++)
        {
            for (; index < res.length; index++)
            {
                let row = res[index];
                let date_label = this.getFormattedDateByGranularity(new Date(row.period), granularity_type);
                if (chart_date_labels[d] !== date_label)
                    break;
                let dimension = await translator(row.dimension);
                if (!dimension_data[dimension])
                    dimension_data[dimension] = new Array(d).fill(0);
                dimension_data[dimension].push(PriceOperation.millicent_to_fiat(row.sum_price_payable))
            }
            for (let key in dimension_data)
            {
                if (dimension_data[key].length !== d + 1)
                    dimension_data[key].push(0);
            }
        }
        let chart_dimension_data: { label: string, data: number[] }[] = Object.keys(dimension_data).map(key => ({ label: key, data: dimension_data[key] }));
        this.setState({ chart_date_labels, chart_dimension_data, loading: false });
    }
    getColumns()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        const dimensionColumnMap: Record<DimensionType, TableColumnInfo> = {
            NONE: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "", "", true), new IDFormatter(namirasoft, new IStorageLocal())),
            WORKSPACE: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "Workspace", "", true), new IDFormatter(namirasoft, new IStorageLocal())),
            PRODUCT: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "Product", "", true), new IDFormatter(namirasoft, new IStorageLocal())),
            RESOURCE_ENTITY: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "Resource Entity", "", true), new StringFormatter(StringFormatterSizeType.TwoWords)),
            RESOURCE_ID: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "Resource ID", "", true), new IDFormatter(namirasoft, new IStorageLocal())),
            USAGE_NAME: BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "dimension", "Usage Name", "", true), new StringFormatter(StringFormatterSizeType.TwoWords)),
        };

        let columns = [];
        let dimension = this.NSBoxEnum_Dimension?.current?.getValueEnumOne(DimensionType, DimensionType.NONE) ?? DimensionType.NONE;
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "period", "Period", "", true), new DateTimeFormatter()))
        if (dimension !== DimensionType.NONE)
            columns.push(dimensionColumnMap[dimension]);
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_count", "Total Count", "Integer", true), new IntegerFormatter(StringFormatterSizeType.TwoWords)));
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_usage_total", "Total Usage", "Integer", true), new MoneyFormatter(StringFormatterSizeType.TwoWords)));
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_token_total", "Total Token", "Integer", true), new IntegerFormatter(StringFormatterSizeType.TwoWords)));
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_price_total", "Total Price", "Money", true), new MoneyFormatter(StringFormatterSizeType.TwoWords)));
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_price_discount", "Total Discount", "Money", true), new MoneyFormatter(StringFormatterSizeType.TwoWords)));
        columns.push(BaseColumnFormatter.getColumn(new BaseMetaColumn(namirasoft.cost.tables.cost, "sum_price_payable", "Total Payable", "Money", true), new MoneyFormatter(StringFormatterSizeType.TwoWords)));
        return columns;
    }
    setQuickPeriod(getFromDate: (date: Date) => Date)
    {
        let to_date = new Date();
        to_date.setHours(0, 0, 0, 0);
        to_date = TimeOperation.daysLater(1, to_date);
        let from_date = getFromDate(to_date);
        this.NSBoxDateTimeRange_StartEnd.current?.setValue({ from: from_date, to: to_date }, this.onChanged);
    }
    override componentDidMount()
    {
        if (this.state.res == null)
            this.onChanged();
    }
    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let granularity_type = this.NSBoxEnum_Granularity?.current?.getValueEnumOne(GranularityType, GranularityType.Day) ?? GranularityType.Day;

        let max_amount = 0;
        for (let i = 0; i < this.state.chart_date_labels.length; i++)
        {
            let column_sum = 0;

            for (let j = 0; j < this.state.chart_dimension_data.length; j++)
                column_sum += this.state.chart_dimension_data[j].data[i];

            if (column_sum > max_amount)
                max_amount = column_sum
        }
        let total_count = this.state.res?.reduce((a, b) => a + b.sum_count, 0) ?? 0;
        let total_usage = this.state.res?.reduce((a, b) => a + b.sum_usage_total, 0) ?? 0;
        let total_token = this.state.res?.reduce((a, b) => a + b.sum_token_total, 0) ?? 0;
        let total_price = this.state.res?.reduce((a, b) => a + b.sum_price_total, 0) ?? 0;
        let total_discount = this.state.res?.reduce((a, b) => a + b.sum_price_discount, 0) ?? 0;
        let total_payable = this.state.res?.reduce((a, b) => a + b.sum_price_payable, 0) ?? 0;
        let len = (this.state.res ?? []).length;
        let avg_amount = 0;
        if (len > 0)
            avg_amount = total_payable / len;
        let default_date_to = new Date();
        default_date_to.setHours(0, 0, 0, 0);
        let default_date_from = TimeOperation.daysAgo(7, default_date_to);
        let content =
            <NSSection classList={[styles.ns_section]}>
                <NSColumn classList={[styles.ns_parameters_column]}>
                    <NSPanel classList={[styles.ns_panel]}>
                        <NSTitle
                            title="Report Parameters"
                            classList={[styles.ns_title]}
                        />
                        <NSTitle
                            title="Time"
                            classList={[styles.ns_subtitle]}
                        />
                        <div className={styles.ns_button_wrapper}>
                            <NSButton
                                title="Last Day"
                                onClick={{
                                    action: () =>
                                    {
                                        try
                                        {
                                            this.setQuickPeriod(d => TimeOperation.daysAgo(1, d));
                                        }
                                        catch (error)
                                        {
                                            this.props.notifier.onError(error as Error);
                                        }
                                    },
                                    showLoading: false
                                }}
                            />
                            <NSButton
                                title="Last Week"
                                onClick={{
                                    action: () =>
                                    {
                                        try
                                        {
                                            this.setQuickPeriod(d => TimeOperation.daysAgo(7, d));
                                        }
                                        catch (error)
                                        {
                                            this.props.notifier.onError(error as Error);
                                        }
                                    },
                                    showLoading: false
                                }}
                            />
                            <NSButton
                                title="Last Month"
                                onClick={{
                                    action: () =>
                                    {
                                        try
                                        {
                                            this.setQuickPeriod(d => TimeOperation.monthsAgo(1, d));
                                        }
                                        catch (error)
                                        {
                                            this.props.notifier.onError(error as Error);
                                        }
                                    },
                                    showLoading: false
                                }}
                            />
                            <NSButton
                                title="Last Year"
                                onClick={{
                                    action: () =>
                                    {
                                        try
                                        {
                                            this.setQuickPeriod(d => TimeOperation.yearsAgo(1, d));
                                        }
                                        catch (error)
                                        {
                                            this.props.notifier.onError(error as Error);
                                        }
                                    },
                                    showLoading: false
                                }}
                            />
                        </div>
                        <NSBoxDateTimeRange 
                            ref={this.NSBoxDateTimeRange_StartEnd}
                            title={"Date Range"}
                            required={false}
                            defaultValue={{ from: default_date_from, to: default_date_to }}
                            onChanged={() =>
                            {
                                this.onChanged()
                            }}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-from-date" }}
                            style={{ width: "100%" }}
                        />
                        <NSTitle
                            title="Granularity"
                            classList={[styles.ns_subtitle]}
                        />
                        <NSBoxEnum<GranularityType>
                            ref={this.NSBoxEnum_Granularity}
                            title="Granularity"
                            multiple={false}
                            required={false}
                            defaultValue={GranularityType.Day}
                            getEnumObject={() => GranularityType}
                            onChanged={this.onChanged}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-granularity" }}
                            style={{ width: "100%" }}
                        />
                        <NSTitle
                            title="Group by"
                            classList={[styles.ns_subtitle]}
                        />
                        <NSBoxEnum<DimensionType>
                            ref={this.NSBoxEnum_Dimension}
                            title="Dimension"
                            multiple={false}
                            required={false}
                            defaultValue={DimensionType.NONE}
                            getEnumObject={() => DimensionType}
                            onChanged={this.onChanged}
                            getTitle={(value) => NamingConvention.auto.convert(value, NamingConvention.Pascal_Case_Space)}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-dimension" }}
                            style={{ width: "100%" }}
                        />

                        <NSTitle
                            title="Filters"
                            classList={[styles.ns_subtitle]}
                        />
                        <NSBoxEntity<WorkspaceRow>
                            ref={this.NSBoxEntity_WrokspaceID}
                            title="Workspace"
                            multiple={true}
                            required={false}
                            table={namirasoft.workspace.tables.workspace}
                            getTitle={(item) => item.name}
                            getValue={(item) => item.id}
                            onChanged={() =>
                            {
                                this.onChanged();
                            }}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-workspace" }}
                            style={{ width: "100%" }}
                        />
                        <NSBoxEntity<ProductRow>
                            ref={this.NSBoxEntity_ProductID}
                            title="Product"
                            multiple={true}
                            required={false}
                            table_name="product"
                            list={async (filters, page, size, sorts) =>
                            {
                                if (!filters)
                                    filters = [];
                                let table = namirasoft.payment.tables.product;
                                let column = namirasoft.payment.tables.product.columns.costable;
                                filters.push(new FilterItem(table, column, false, FilterItemOperator.all.equals, "1"));
                                return this.props.servers.payment.product.List(filters, page, size, sorts, null);
                            }}
                            getTitle={(item) => item.name}
                            getValue={(item) => item.id}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-product" }}
                            style={{ width: "100%" }}
                        />
                        <NSBoxBaseCombo<{ resource_entity: string }>
                            ref={this.NSBoxBaseCombo_Entity}
                            title="Entity"
                            multiple={true}
                            required={false}
                            getItems={async (_, __, page, size, sorts) =>
                            {
                                const res = await this.props.servers.cost.cost.ResourceEntityList([], page, size, sorts, null);
                                return { rows: res, count: res.length }
                            }}
                            getTitle={(item) => item.resource_entity}
                            getValue={(item) => item.resource_entity}
                            onChanged={(box) =>
                            {
                                this.setState(() => ({ selected_resource_entities: [...box.getValueAll()] }))
                                this.onChanged();
                            }}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-entity" }}
                            style={{ width: "100%" }}
                        />
                        {
                            this.state.selected_resource_entities.length === 1 &&
                            (
                                <NSBoxBaseCombo<{ resource_id: string }>
                                    ref={this.NSBoxBaseCombo_ResourceID}
                                    title="Resource"
                                    multiple={true}
                                    required={false}
                                    getItems={async (_, __, page, size, sorts) =>
                                    {
                                        let name = this.state.selected_resource_entities[0];
                                        const res = await this.props.servers.cost.cost.ResourceIDList(name, [], page, size, sorts, null);
                                        return { rows: res, count: res.length }
                                    }}
                                    getTitle={(item) => item.resource_id}
                                    getValue={(item) => item.resource_id}
                                    onChanged={() =>
                                    {
                                        this.onChanged();
                                    }}
                                    getSort={() => null}
                                    info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-resource" }}
                                    style={{ width: "100%" }}
                                />
                            )
                        }
                        <NSBoxCombo
                            ref={this.NSBoxCombo_Usage}
                            title="Usage"
                            multiple={true}
                            required={false}
                            getOptions={() =>
                                Object.values(Usage)
                                    .filter(u => Boolean(u.name))
                                    .map(u => ({ value: u.name, title: u.name })) ?? []
                            }
                            onChanged={() =>
                            {
                                this.onChanged();
                            }}
                            info={{ link: "https://namirasoft.com/cost/console-guide/explorer/#field-usage" }}
                            style={{ width: "100%" }}
                        />
                    </NSPanel>
                </NSColumn>

                <NSColumn classList={[styles.ns_report_column]}>
                    <NSPanel style={{ width: "100%", padding: "1.5rem 1rem", gap: '1.5rem' }}>
                        <NSTitle
                            title="Cost and Usage overview"
                            classList={[styles.ns_title]}
                        />
                        <NSRow style={{ justifyContent: "flex-start" }}>
                            <NSLabel
                                title="Total Count:"
                                description={new IntegerFormatter().format(total_count)}
                                classList={[styles.ns_label]}
                            />
                            <NSLabel
                                title="Total Usage:"
                                description={new MoneyFormatter().format(total_usage)}
                                classList={[styles.ns_label]}
                            />
                            <NSLabel
                                title="Total Token:"
                                description={new IntegerFormatter().format(total_token)}
                                classList={[styles.ns_label]}
                            />
                            <NSLabel
                                title="Total Price:"
                                description={new MoneyFormatter().format(total_price)}
                                classList={[styles.ns_label]}
                            />
                        </NSRow>
                        <NSRow style={{ justifyContent: "flex-start" }}>
                            <NSLabel
                                title="Total Discount:"
                                description={new MoneyFormatter().format(total_discount)}
                                classList={[styles.ns_label]}
                            />
                            <NSLabel
                                title="Total Payable:"
                                description={new MoneyFormatter().format(total_payable)}
                                classList={[styles.ns_label]}
                            />
                            <NSLabel
                                title={`Average Cost of ${granularity_type}:`}
                                description={new MoneyFormatter().format(avg_amount)}
                                classList={[styles.ns_label]}
                            />
                        </NSRow>
                    </NSPanel>

                    <NSPanel style={{ width: "100%", paddingTop: "2rem" }}>
                        {this.state.loading && <NSLoading />}

                        {
                            !this.state.loading && this.state.chart_dimension_data.length === 0 &&
                            <NSNoData lable="No Data" style={{ paddingBottom: "2rem" }} />
                        }

                        {
                            !this.state.loading && this.state.chart_dimension_data.length > 0 &&
                            <NSChartBar
                                dataset={this.state.chart_dimension_data}
                                labels={this.state.chart_date_labels}
                                range={{
                                    min: 0,
                                    max: Math.ceil(max_amount * 1.1),
                                }}
                                mode={"stacked"}
                                style={{ minWidth: "100%" }}
                            />
                        }
                    </NSPanel>

                    <NSRow style={{ gap: 0 }}>
                        <NSTable<GroupedResponseRow>
                            ref={this.NSTable_Result}
                            checkbox
                            columns={this.getColumns()}
                            name="Cost"
                            getRows={async () =>
                            {
                                let rows = this.state.res ?? [];
                                return { count: rows.length, rows };
                            }}
                            getRowKey={(row) => (row.index + 1).toString()}
                            style={{ width: "100%", height: "auto" }}
                            ui={{ pagination: false }}
                        />
                    </NSRow>
                </NSColumn>
            </NSSection>;

        return (
            <CostLayout
                {...this.props}
                action={{
                    title: "Explorer",
                    description: "This section allows you to view detailed costs and graphs for usage-based products, with filter options such as time range, granularity, product, entity, and usage.",
                    menus: {},
                }}
                pages={{
                    entity: CostEntities.cost(this.props, []),
                }}
            >
                {content}
            </CostLayout >
        );
    }
}
export default CostExplorerPage;